"use client";

import { useEffect, useCallback, useState, useRef, Fragment } from "react";
import { compareFolders, type SessionListItem, type FolderResponse, type SearchResponse } from "@/app/lib/types";
import { searchSessions } from "@/app/lib/api";
import RecentTab from "./RecentTab";
import FoldersTab from "./FoldersTab";
import SessionItem from "./SessionItem";
import NewFolderDialog from "../common/NewFolderDialog";

interface HistorySheetProps {
  sessions: SessionListItem[];
  folders: FolderResponse[];
  activeSessionId?: string;
  onSessionClick: (id: string) => void;
  onSessionDelete: (id: string) => void;
  onSessionRename: (id: string, title: string) => void;
  onSessionFlag: (id: string) => void;
  onMoveToFolder?: (id: string) => void;
  onMoveSessionToFolder?: (sessionId: string, folderId: string) => void;
  onMoveToNewFolder?: (sessionId: string) => void;
  onShare?: (id: string) => void;
  onNewChatInFolder: (folderId: string) => void;
  onCreateFolder: () => void;
  onRenameFolder: (id: string, name: string) => void;
  onSetFolderSeparator?: (id: string, value: boolean) => void;
  onDeleteFolder: (id: string) => void;
  onReorderFolders?: (orderedIds: string[]) => void;
  currentUserEmail?: string;
  isLoading?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onBulkDelete?: (ids: string[]) => Promise<void>;
  onBulkMoveToFolder?: (ids: string[], folderId: string) => Promise<void>;
  onBulkMoveToNewFolder?: (ids: string[], name: string) => Promise<void>;
  newlyCreatedFolderId?: string | null;
  onAutoEditConsumed?: () => void;
}

export default function HistorySheet({
  sessions,
  folders,
  activeSessionId,
  onSessionClick,
  onSessionDelete,
  onSessionRename,
  onSessionFlag,
  onMoveToFolder,
  onMoveSessionToFolder,
  onMoveToNewFolder,
  onShare,
  onNewChatInFolder,
  onCreateFolder,
  onRenameFolder,
  onSetFolderSeparator,
  onDeleteFolder,
  onReorderFolders,
  currentUserEmail,
  isLoading,
  isOpen,
  onClose,
  onBulkDelete,
  onBulkMoveToFolder,
  onBulkMoveToNewFolder,
  newlyCreatedFolderId,
  onAutoEditConsumed,
}: HistorySheetProps) {
  const [activeTab, setActiveTab] = useState<"recent" | "folders">("recent");
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResponse | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleCancelSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setShowDeleteConfirm(true);
  }, [selectedIds]);

  const confirmDelete = useCallback(async () => {
    setShowDeleteConfirm(false);
    if (onBulkDelete) {
      await onBulkDelete(Array.from(selectedIds));
    }
    setSelectedIds(new Set());
    setSelectionMode(false);
  }, [selectedIds, onBulkDelete]);

  const [showBulkMoveFolder, setShowBulkMoveFolder] = useState(false);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults(null); setIsSearching(false); return; }
    setIsSearching(true);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const results = await searchSessions(searchQuery.trim());
        setSearchResults(results);
      } catch { /* ignore */ }
      setIsSearching(false);
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [searchQuery]);

  // Clear search and selection when sheet closes
  useEffect(() => {
    if (!isOpen) { setSearchQuery(""); setSearchResults(null); setSelectionMode(false); setSelectedIds(new Set()); }
  }, [isOpen]);

  // Close on Escape
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  const handleToggleFolder = useCallback((id: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSessionClick = useCallback(
    (id: string) => {
      onSessionClick(id);
      onClose();
    },
    [onSessionClick, onClose]
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className="sidebar-overlay"
        style={{
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? "auto" : "none",
          transition: "opacity var(--transition-normal)",
        }}
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 50,
          transform: isOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform var(--transition-normal)",
          width: "var(--sidebar-width)",
          background: "var(--surface)",
          borderRight: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {/* Header: Select + Close */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 8px 0",
          }}
        >
          <button
            onClick={selectionMode ? handleCancelSelection : () => setSelectionMode(true)}
            style={{
              background: "none",
              border: "none",
              color: selectionMode ? "var(--error)" : "var(--text-secondary)",
              fontSize: "12px",
              cursor: "pointer",
              padding: "4px 6px",
            }}
          >
            {selectionMode ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <path d="M5 6.5l1.5 1.5L9 5" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="2" rx="0.5" />
                <rect x="14" y="8" width="7" height="2" rx="0.5" />
                <rect x="14" y="14" width="7" height="2" rx="0.5" />
                <rect x="14" y="19" width="7" height="2" rx="0.5" />
              </svg>
            )}
          </button>
          <button
            className="btn-ghost"
            onClick={onClose}
            aria-label="Close history"
            style={{ padding: "6px" }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        {/* Search bar */}
        <div style={{ padding: "0 12px 6px" }}>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder="Search chats..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "7px 8px 7px 28px",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                background: "var(--bg)",
                color: "var(--text-primary)",
                fontSize: "13px",
                outline: "none",
              }}
            />
            <svg width="13" height="13" viewBox="0 0 16 16" fill="var(--text-tertiary)" style={{ position: "absolute", left: "8px", top: "50%", transform: "translateY(-50%)" }}>
              <path d="M11.742 10.344a6.5 6.5 0 10-1.397 1.398h-.001l3.85 3.85a1 1 0 001.415-1.414l-3.85-3.85zm-5.242.156a5 5 0 110-10 5 5 0 010 10z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                style={{ position: "absolute", right: "6px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text-tertiary)", cursor: "pointer", padding: "2px" }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Tab bar + new chat */}
        <div
          style={{
            padding: "4px 12px 8px",
            display: searchQuery ? "none" : "flex",
            alignItems: "center",
            gap: "8px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              flex: 1,
              background: "var(--bg)",
              borderRadius: "var(--radius-md)",
              padding: "2px",
              gap: "2px",
            }}
          >
            <button
              onClick={() => setActiveTab("recent")}
              style={{
                flex: 1,
                padding: "6px 0",
                border: "none",
                borderRadius: "var(--radius-sm)",
                background:
                  activeTab === "recent"
                    ? "var(--surface-hover)"
                    : "transparent",
                color:
                  activeTab === "recent"
                    ? "var(--text-primary)"
                    : "var(--text-secondary)",
                fontSize: "13px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Recent
            </button>
            <button
              onClick={() => setActiveTab("folders")}
              style={{
                flex: 1,
                padding: "6px 0",
                border: "none",
                borderRadius: "var(--radius-sm)",
                background:
                  activeTab === "folders"
                    ? "var(--surface-hover)"
                    : "transparent",
                color:
                  activeTab === "folders"
                    ? "var(--text-primary)"
                    : "var(--text-secondary)",
                fontSize: "13px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Folders
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          {searchQuery ? (
            // Search results
            <div style={{ padding: "8px 0" }}>
              {isSearching && !searchResults && (
                <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
                  <div className="spinner" />
                </div>
              )}
              {searchResults && (
                <>
                  {searchResults.title_matches.length > 0 && (
                    <div>
                      <div style={{ padding: "4px 16px", fontSize: "11px", color: "var(--text-tertiary)", fontWeight: 600, textTransform: "uppercase" }}>In titles</div>
                      {searchResults.title_matches.map((m) => (
                        <div key={m.id} className="session-item" onClick={() => handleSessionClick(m.id)} style={{ cursor: "pointer", padding: "6px 16px" }}>
                          <div style={{ fontSize: "13px", color: "var(--text-primary)" }}>{m.title}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchResults.content_matches.length > 0 && (
                    <div>
                      <div style={{ padding: "8px 16px 4px", fontSize: "11px", color: "var(--text-tertiary)", fontWeight: 600, textTransform: "uppercase" }}>In responses</div>
                      {searchResults.content_matches.map((m) => (
                        <div key={m.id} className="session-item" onClick={() => handleSessionClick(m.id)} style={{ cursor: "pointer", padding: "6px 16px" }}>
                          <div style={{ fontSize: "13px", color: "var(--text-primary)" }}>{m.title}</div>
                          <div style={{ fontSize: "12px", color: "var(--text-tertiary)", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.snippet}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchResults.title_matches.length === 0 && searchResults.content_matches.length === 0 && (
                    <div style={{ textAlign: "center", padding: "24px", color: "var(--text-tertiary)", fontSize: "13px" }}>No results found</div>
                  )}
                </>
              )}
            </div>
          ) : isLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
              <div className="spinner" />
            </div>
          ) : activeTab === "recent" ? (
            <RecentTab
              sessions={sessions}
              folders={folders}
              activeSessionId={activeSessionId}
              onSessionClick={handleSessionClick}
              onSessionDelete={onSessionDelete}
              onSessionRename={onSessionRename}
              onSessionFlag={onSessionFlag}
              onMoveToFolder={onMoveToFolder}
              onMoveSessionToFolder={onMoveSessionToFolder}
              onMoveToNewFolder={onMoveToNewFolder}
              onShare={onShare}
              currentUserEmail={currentUserEmail}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
            />
          ) : (
            <FoldersTab
              folders={folders}
              sessions={sessions}
              activeSessionId={activeSessionId}
              expandedFolders={expandedFolders}
              onToggleFolder={handleToggleFolder}
              onSessionClick={handleSessionClick}
              onSessionDelete={onSessionDelete}
              onSessionRename={onSessionRename}
              onSessionFlag={onSessionFlag}
              onMoveToFolder={onMoveToFolder}
              onMoveSessionToFolder={onMoveSessionToFolder}
              onMoveToNewFolder={onMoveToNewFolder}
              onShare={onShare}
              onNewChatInFolder={onNewChatInFolder}
              onRenameFolder={onRenameFolder}
              onSetFolderSeparator={onSetFolderSeparator}
              onDeleteFolder={onDeleteFolder}
              onReorderFolders={onReorderFolders}
              onCreateFolder={onCreateFolder}
              currentUserEmail={currentUserEmail}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              newlyCreatedFolderId={newlyCreatedFolderId}
              onAutoEditConsumed={onAutoEditConsumed}
            />
          )}
        </div>

        {/* Bulk action buttons */}
        {selectionMode && selectedIds.size > 0 && (
          <div style={{ padding: "8px 12px", borderTop: "1px solid var(--border)", display: "flex", gap: "8px" }}>
            <button
              onClick={() => setShowBulkMoveFolder(true)}
              style={{
                flex: 1,
                padding: "8px 0",
                background: "var(--surface-hover)",
                color: "var(--text)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
                fontSize: "13px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Move ({selectedIds.size})
            </button>
            <button
              onClick={handleDeleteSelected}
              style={{
                flex: 1,
                padding: "8px 0",
                background: "var(--error)",
                color: "white",
                border: "none",
                borderRadius: "var(--radius-md)",
                fontSize: "13px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Delete ({selectedIds.size})
            </button>
          </div>
        )}

        {/* Bulk move folder picker */}
        {showBulkMoveFolder && (
          <>
          <div onClick={() => setShowBulkMoveFolder(false)} style={{ position: "fixed", inset: 0, zIndex: 49 }} />
          <div style={{
            position: "absolute", bottom: "60px", left: "12px",
            width: "160px",
            background: "var(--surface)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-lg)", padding: "4px", zIndex: 50,
            boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
            maxHeight: "400px", overflowY: "auto",
          }}>
            <button
              className="btn-ghost"
              style={{ width: "100%", justifyContent: "flex-start", padding: "8px 12px", fontSize: "14px", fontWeight: 600, color: "var(--accent, var(--text))" }}
              onClick={() => { setShowBulkMoveFolder(false); setShowNewFolderDialog(true); }}
            >
              + New Folder…
            </button>
            <div style={{ height: "1px", background: "var(--border)", margin: "4px 0" }} />
            {[...folders].sort(compareFolders).map((folder) => (
              <Fragment key={folder.id}>
                {folder.separator_before && <div style={{ height: "2px", background: "#484848", borderRadius: "1px", margin: "4px 8px" }} />}
                <button
                  className="btn-ghost"
                  style={{ width: "100%", justifyContent: "flex-start", padding: "8px 12px", fontSize: "14px" }}
                  onClick={async () => {
                    if (onBulkMoveToFolder) {
                      await onBulkMoveToFolder(Array.from(selectedIds), folder.id);
                    }
                    setShowBulkMoveFolder(false);
                    setSelectedIds(new Set());
                    setSelectionMode(false);
                  }}
                >
                  {folder.name}
                </button>
              </Fragment>
            ))}
          </div>
          </>
        )}

        <NewFolderDialog
          isOpen={showNewFolderDialog}
          onClose={() => setShowNewFolderDialog(false)}
          onCreate={async (name) => {
            const ids = Array.from(selectedIds);
            if (onBulkMoveToNewFolder) await onBulkMoveToNewFolder(ids, name);
            setSelectedIds(new Set());
            setSelectionMode(false);
          }}
        />

        {/* Delete confirmation dialog */}
        {showDeleteConfirm && (
          <>
            <div onClick={() => setShowDeleteConfirm(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 99 }} />
            <div style={{
              position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
              background: "var(--surface)", border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)", padding: "20px", zIndex: 100,
              minWidth: "240px", textAlign: "center",
            }}>
              <p style={{ margin: "0 0 16px", fontSize: "14px", color: "var(--text)" }}>
                Delete {selectedIds.size} item{selectedIds.size !== 1 ? "s" : ""}?
              </p>
              <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                <button onClick={() => setShowDeleteConfirm(false)} style={{ padding: "6px 16px", background: "var(--surface-hover)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", fontSize: "13px", cursor: "pointer" }}>Cancel</button>
                <button onClick={confirmDelete} style={{ padding: "6px 16px", background: "var(--error)", color: "white", border: "none", borderRadius: "var(--radius-md)", fontSize: "13px", cursor: "pointer" }}>Delete</button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
